import { DateUtil } from './index';
import { getBusinessInfo, serverTimeToLocal, getDatetimeFormat as getDateTimeFormat, getWeekStartDay } from '@platform/api';

const withTimeComponent = new Set(['NCTZDatePickClientTime', 'DateTimePickerBetween', 'datetimepicker']);

function getDefaultBaseDate(){
    // return getBusinessInfo()?.businessDate?.split(' ')?.[0] || DateUtil.formatValue(new Date(), { format : 'YYYY-MM-DD' });
    return getBusinessInfo()?.businessDate || DateUtil.formatValue(new Date());
}

function getDefaultBaseTime(){
    return getBusinessInfo()?.businessDate?.split(' ')?.[1] || DateUtil.formatValue(new Date(), { format : 'HH:mm:ss' });
}

function getDayValues(base, diff){
    let now = DateUtil.validate(base) ? DateUtil.toDayjs(base).toDate() : new Date();
    let day = DateUtil.addDate(now, diff);
    let str = DateUtil.formatValueForDate(day);
    return [str, str];
}

function getMonthValue(base, diff){
    let now = DateUtil.validate(base) ? DateUtil.toDayjs(base).toDate() : new Date();
    let month = DateUtil.addMonth(now, diff);
    let start = DateUtil.getMonthFirstDay(month);
    let end = DateUtil.getMonthLastDay(month);
    let startStr = DateUtil.formatValueForDate(start);
    let endStr = DateUtil.formatValueForDate(end);
    return [startStr, endStr];
}

function getWeekValues(base, weekStart, diff){
    let now = DateUtil.validate(base) ? DateUtil.toDayjs(base).toDate() : new Date();
    let week = DateUtil.addWeek(now, diff);
    let start = DateUtil.getWeekFirstDay(week, weekStart);
    let end = DateUtil.getWeekLastDay(week, weekStart);
    let startStr = DateUtil.formatValueForDate(start);
    let endStr = DateUtil.formatValueForDate(end);
    return [startStr, endStr];
}

function getQuarterValues(base, diff){
    let now = DateUtil.validate(base) ? DateUtil.toDayjs(base).toDate() : new Date();
    let quarter = DateUtil.addQuarter(now, diff);
    let start = DateUtil.getQuarterFirstDay(quarter);
    let end = DateUtil.getQuarterLastDay(quarter);
    let startStr = DateUtil.formatValueForDate(start);
    let endStr = DateUtil.formatValueForDate(end);
    return [startStr, endStr];
}

function getYearValues(base, diff){
    let now = DateUtil.validate(base) ? DateUtil.toDayjs(base).toDate() : new Date();
    let year = DateUtil.addYear(now, diff);
    let start = DateUtil.getYearFirstDay(year);
    let end = DateUtil.getYearLastDay(year);
    let startStr = DateUtil.formatValueForDate(start);
    let endStr = DateUtil.formatValueForDate(end);
    return [startStr, endStr];

}

function getFunctionValue(functionName, attr){// functionName可能带时间
    const { range, picker, functions = {} } = attr;
    
    let matched = functionName.match(/(#[^#]*#)/);
    if(!matched) return range ? [functionName, functionName] : functionName;

    let name = matched[1];
    const defaultFunctions = getDefaultDateFunction(attr);

    if(picker && (name === '#future#' || name === '#past#')){ // 初始化里不设置picker值，每次picker不同，动态更新
        defaultFunctions[name].type = picker;
    }

    if(defaultFunctions[name]){ //内置日期函数 
        let values = defaultFunctions[name].value();
        if(Array.isArray(values)){
            return values.map(value => functionName.replace(name, value));
        } else {
            return functionName.replace(name, values);
        }
    
    } else if(functions[name]){ //自定义日期函数 不需要格式化 也可能是任意字符串（非日期字符串）
        return functions[name]?.value();

    } else {
        console.warn(`无法识别函数名${name}`);
        return null;
    }
}

const rangeForSingleDateFunction = new Set([
    '#month(-1)#',
    '#month(0)#',
    '#month(1)#',
    '#week(-1)#',
    '#week(0)#',
    '#week(1)#',
    '#quarter(-1)#',
    '#quarter(0)#',
    '#quarter(1)#',
    '#year(-1)#',
    '#year(0)#',
    '#year(1)#',
]);

function getFunctionDisplay(functionName, attr){// functionName可能带时间
    let { range, picker, functions = {}, itemtype, format, singleValue } = attr;

    let matched = functionName.match(/(#[^#]*#)/);
    if(!matched) return range ? [functionName, functionName] : functionName;

    let name = matched[1];
    let hasTime = picker === 'dateTime' && name != '#future#' && name != '#past#';
    if(itemtype){ //兼容查询区
        hasTime = name != '#future#' && name != '#past#' && withTimeComponent.has(attr.itemtype);
    }
    const defaultFunctions = getDefaultDateFunction(attr);

    if(picker && (name === '#future#' || name === '#past#')){ // 初始化里不设置picker值，每次picker不同，动态更新
        defaultFunctions[name].type = picker;
    }

    let globalFormat = getDateTimeFormat(Array.isArray(format) ? format[0] : format || 'YYYY-MM-DD');

    if(defaultFunctions[name]){ //内置日期函数 
        let values = defaultFunctions[name].value();
        let displays = defaultFunctions[name].display(values);
        if(!hasTime) return displays; //没有时间的不需要特殊处理

        globalFormat = getDateTimeFormat(Array.isArray(format) ? format[0] : format || 'YYYY-MM-DD HH:mm:ss');
        let dateTimeString = '2021-05-01 '.concat(functionName.split(' ')[1]);
        let timeString = DateUtil.toDayjs(dateTimeString)?.format(globalFormat.split(' ')[1]); //转成对象再转成字符串

        if(Array.isArray(displays)){
            displays = displays.map(display => {
                if(display.indexOf(')') !== -1){
                    return display.split(')')[0].concat(` ${timeString})`);
                }
                return display;
            });
        } else if(!range && !singleValue && rangeForSingleDateFunction.has(name)){
            displays = displays.replace(name, displays).replace('~', ` ${timeString}~`).replace(')', ` ${timeString})`);
        } else {
            if(displays.indexOf(')') !== -1){
                displays = displays.split(')')[0].concat(` ${timeString})`);
            }
        }
        return displays;
    
    } else if(functions[name]){ //自定义日期函数 不需要格式化 也可能是任意字符串（非日期字符串）
        return functions[name]?.display?.(functions[name]?.value?.());
    } else {
        console.warn(`无法识别函数名${name}`);
        return null;
    }
}

function getDefaultDateFunction(attr){
    let { isconverttimezones, multiLang = {}, locale = {}, format = 'YYYY-MM-DD', range, singleValue, picker } = attr;

    let weekStart = getWeekStartDay();
    let base = serverTimeToLocal(DateUtil.toDayjs(getDefaultBaseDate() || new Date()), isconverttimezones);
    const nameAdapter = {
        '#day(-1)#': multiLang['page-search-0060'] || locale['0002'],
        '#day(0)#': multiLang['page-search-0061'] || locale['0003'],
        '#day(1)#': multiLang['page-search-0062'] || locale['0004'],
        '#month(-1)#': multiLang['page-search-0063'] || locale['0005'],
        '#month(0)#': multiLang['page-search-0064'] || locale['0006'],
        '#month(1)#': multiLang['page-search-0065'] || locale['0007'],
        '#week(-1)#': multiLang['page-search-0066'] || locale['0008'],
        '#week(0)#': multiLang['page-search-0067'] || locale['0009'],
        '#week(1)#': multiLang['page-search-0068'] || locale['0010'],
        '#quarter(-1)#': multiLang['page-search-0069'] || locale['0011'],
        '#quarter(0)#': multiLang['page-search-0070'] || locale['0012'],
        '#quarter(1)#': multiLang['page-search-0071'] || locale['0013'],
        '#year(-1)#': multiLang['page-search-0072'] || locale['0014'],
        '#year(0)#': multiLang['page-search-0073'] || locale['0015'],
        '#year(1)#': multiLang['page-search-0074'] || locale['0016'],
        '#lastDayOfMonth#': multiLang['page-search-0075'] || locale['0017'], 
        '#lastDayOfWeek#': multiLang['page-search-0076'] || locale['0018'], 
        '#finalDayOfLastMonth#': multiLang['page-search-0077'] || locale['0019'], 
        '#past#': multiLang['page-search-0100'] || locale['0020'],
        '#future#': multiLang['page-search-0101'] || locale['0021'],
        '#lastDayOfYear#': multiLang['page-search-0102'] || locale['0032'],
    };
    let globalFormat = getDateTimeFormat(Array.isArray(format) ? format[0] : format);

    const rangeDisplayFunction = (name, values) => {
        return values.map(value => {
            if(value.indexOf(' ') === -1){ //没有时间部分，需要拼上，不然到了serverToLocal接口，会默认用当前时间，时区改变时有影响
                value = value.concat(' ').concat(getDefaultBaseTime());
            }
            // return `${name}(${serverTimeToLocal(DateUtil.toDayjs(value), isconverttimezones).format(globalFormat)})`;
            return `${name}(${DateUtil.toDayjs(value).format(globalFormat)})`;
        });
    };

    const singleDisplayFunction = (name, value) => {
        if(value.indexOf(' ') === -1){ //没有时间部分，需要拼上，不然到了serverToLocal接口，会默认用当前时间，时区改变时有影响
            value = value.concat(' ').concat(getDefaultBaseTime());
        }
        // return `${name}(${serverTimeToLocal(DateUtil.toDayjs(value), isconverttimezones).format(globalFormat)})`;
        return `${name}(${DateUtil.toDayjs(value).format(globalFormat)})`;
    };

    const rangeForSingleDisplayFunction = (name, values) => {
        values = values.map(value => {
            if(value.indexOf(' ') === -1){ //没有时间部分，需要拼上，不然到了serverToLocal接口，会默认用当前时间，时区改变时有影响
                value = value.concat(' ').concat(getDefaultBaseTime());
            }
            return value;
        });
        // return `${name}(${serverTimeToLocal(DateUtil.toDayjs(values[0]), isconverttimezones).format(globalFormat)}~${serverTimeToLocal(DateUtil.toDayjs(values[1]), isconverttimezones).format(globalFormat)})`;
        return `${name}(${DateUtil.toDayjs(values[0]).format(globalFormat)}~${DateUtil.toDayjs(values[1]).format(globalFormat)})`;
    };

    const defaultFunctions = {
        '#day(-1)#': { //昨日
            name: nameAdapter['#day(-1)#'],
            value: () => {
                let values = getDayValues(base, -1);
                if(!range && singleValue){ //只有非start/end单点日期返回单个值
                    return singleValue === 'end' ? values[1] : values[0];
                }
                return values;
            },
            display: values => {
                if(range){ //区间的value是数组
                    return rangeDisplayFunction(nameAdapter['#day(-1)#'], values);
                } else if(singleValue) { //start/end的value是单个
                    return singleDisplayFunction(nameAdapter['#day(-1)#'], values);
                } else { //非start/end的value是数组，但需要显示成单个
                    return singleDisplayFunction(nameAdapter['#day(-1)#'], values[0]);
                }
            },
        }, 
        '#day(0)#': { //今日
            name: nameAdapter['#day(0)#'],
            value: () => {
                let values = getDayValues(base, 0);
                if(!range && singleValue){ //只有非start/end单点日期返回单个值
                    return singleValue === 'end' ? values[1] : values[0];
                }
                return values;
            },
            display: values => {
                if(range){ //区间的value是数组
                    return rangeDisplayFunction(nameAdapter['#day(0)#'], values);
                } else if(singleValue) { //start/end的value是单个
                    return singleDisplayFunction(nameAdapter['#day(0)#'], values);
                } else { //非start/end的value是数组，但需要显示成单个
                    return singleDisplayFunction(nameAdapter['#day(0)#'], values[0]);
                }
            },
        }, 
        '#day(1)#': { //明日
            name: nameAdapter['#day(1)#'],
            value: () => {
                let values = getDayValues(base, 1);
                if(!range && singleValue){ //只有非start/end单点日期返回单个值
                    return singleValue === 'end' ? values[1] : values[0];
                }
                return values;
            },
            display: values => {
                if(range){ //区间的value是数组
                    return rangeDisplayFunction(nameAdapter['#day(1)#'], values);
                } else if(singleValue) { //start/end的value是单个
                    return singleDisplayFunction(nameAdapter['#day(1)#'], values);
                } else { //非start/end的value是数组，但需要显示成单个
                    return singleDisplayFunction(nameAdapter['#day(1)#'], values[0]);
                }
            },
        }, 
        '#month(-1)#': { //上月
            name: nameAdapter['#month(-1)#'],
            value: () => {
                let values = getMonthValue(base, -1);
                if(!range && singleValue){
                    if(singleValue === 'end') return values[1];
                    else if(singleValue === 'start') return values[0];
                }
                return values;
            },
            display: values => {
                if(range){
                    return rangeDisplayFunction(nameAdapter['#month(-1)#'], values);
                } else if(singleValue){
                    return singleDisplayFunction(nameAdapter['#month(-1)#'], values);
                } else { //单点日期的区间函数显示
                    return rangeForSingleDisplayFunction(nameAdapter['#month(-1)#'], values);
                }
            },
        }, 
        '#month(0)#': { //本月
            name: nameAdapter['#month(0)#'],
            value: () => {
                let values = getMonthValue(base, 0);
                if(!range && singleValue){
                    if(singleValue === 'end') return values[1];
                    else if(singleValue === 'start') return values[0];
                }
                return values;
            },
            display: values => {
                if(range){
                    return rangeDisplayFunction(nameAdapter['#month(0)#'], values);
                } else if(singleValue){
                    return singleDisplayFunction(nameAdapter['#month(0)#'], values);
                } else {
                    return rangeForSingleDisplayFunction(nameAdapter['#month(0)#'], values);
                }
            },
        }, 
        '#month(1)#': { //下月
            name: nameAdapter['#month(1)#'],
            value: () => {
                let values = getMonthValue(base, 1);
                if(!range && singleValue){
                    if(singleValue === 'end') return values[1];
                    else if(singleValue === 'start') return values[0];
                }
                return values;
            }, 
            display: values => {
                if(range){
                    return rangeDisplayFunction(nameAdapter['#month(1)#'], values);
                } else if(singleValue){
                    return singleDisplayFunction(nameAdapter['#month(1)#'], values);
                } else {
                    return rangeForSingleDisplayFunction(nameAdapter['#month(1)#'], values);
                }
            },
        }, 
        '#week(-1)#': { //上周
            name: nameAdapter['#week(-1)#'],
            value: () => {
                let values = getWeekValues(base, weekStart, -1);
                if(!range && singleValue){
                    if(singleValue === 'end') return values[1];
                    else if(singleValue === 'start') return values[0];
                }
                return values;
            }, 
            display: values => {
                if(range){
                    return rangeDisplayFunction(nameAdapter['#week(-1)#'], values);
                } else if(singleValue){
                    return singleDisplayFunction(nameAdapter['#week(-1)#'], values);
                } else {
                    return rangeForSingleDisplayFunction(nameAdapter['#week(-1)#'], values);
                }
            },
        }, 
        '#week(0)#': { //本周
            name: nameAdapter['#week(0)#'],
            value: () => {
                let values = getWeekValues(base, weekStart, 0);
                if(!range && singleValue){
                    if(singleValue === 'end') return values[1];
                    else if(singleValue === 'start') return values[0];
                }
                return values;
            },
            display: values => {
                if(range){
                    return rangeDisplayFunction(nameAdapter['#week(0)#'], values);
                } else if(singleValue){
                    return singleDisplayFunction(nameAdapter['#week(0)#'], values);
                } else {
                    return rangeForSingleDisplayFunction(nameAdapter['#week(0)#'], values);
                }
            },
        }, 
        '#week(1)#': { //下周
            name: nameAdapter['#week(1)#'],
            value: () => {
                let values = getWeekValues(base, weekStart, 1);
                if(!range && singleValue){
                    if(singleValue === 'end') return values[1];
                    else if(singleValue === 'start') return values[0];
                }
                return values;
            },
            display: values => {
                if(range){
                    return rangeDisplayFunction(nameAdapter['#week(1)#'], values);
                } else if(singleValue){
                    return singleDisplayFunction(nameAdapter['#week(1)#'], values);
                } else {
                    return rangeForSingleDisplayFunction(nameAdapter['#week(1)#'], values);
                }
            },
        }, 
        '#quarter(-1)#': { //上季
            name: nameAdapter['#quarter(-1)#'],
            value: () => {
                let values = getQuarterValues(base, -1);
                if(!range && singleValue){
                    if(singleValue === 'end') return values[1];
                    else if(singleValue === 'start') return values[0];
                }
                return values;
            },
            display: values => {
                if(range){
                    return rangeDisplayFunction(nameAdapter['#quarter(-1)#'], values);
                } else if(singleValue){
                    return singleDisplayFunction(nameAdapter['#quarter(-1)#'], values);
                } else {
                    return rangeForSingleDisplayFunction(nameAdapter['#quarter(-1)#'], values);
                }
            },
        }, 
        '#quarter(0)#': { //本季
            name: nameAdapter['#quarter(0)#'],
            value: () => {
                let values = getQuarterValues(base, 0);
                if(!range && singleValue){
                    if(singleValue === 'end') return values[1];
                    else if(singleValue === 'start') return values[0];
                }
                return values;
            }, 
            display: values => {
                if(range){
                    return rangeDisplayFunction(nameAdapter['#quarter(0)#'], values);
                } else if(singleValue){
                    return singleDisplayFunction(nameAdapter['#quarter(0)#'], values);
                } else {
                    return rangeForSingleDisplayFunction(nameAdapter['#quarter(0)#'], values);
                }
            },
        }, 
        '#quarter(1)#': { //下季
            name: nameAdapter['#quarter(1)#'],
            value: () => {
                let values = getQuarterValues(base, 1);
                if(!range && singleValue){
                    if(singleValue === 'end') return values[1];
                    else if(singleValue === 'start') return values[0];
                }
                return values;
            }, 
            display: values => {
                if(range){
                    return rangeDisplayFunction(nameAdapter['#quarter(1)#'], values);
                } else if(singleValue){
                    return singleDisplayFunction(nameAdapter['#quarter(1)#'], values);
                } else {
                    return rangeForSingleDisplayFunction(nameAdapter['#quarter(1)#'], values);
                }
            },
        }, 
        '#year(-1)#': { //去年
            name: nameAdapter['#year(-1)#'],
            value: () => {
                let values = getYearValues(base, -1);
                if(!range && singleValue){
                    if(singleValue === 'end') return values[1];
                    else if(singleValue === 'start') return values[0];
                }
                return values;
            }, 
            display: values => {
                if(range){
                    return rangeDisplayFunction(nameAdapter['#year(-1)#'], values);
                } else if(singleValue){
                    return singleDisplayFunction(nameAdapter['#year(-1)#'], values);
                } else {
                    return rangeForSingleDisplayFunction(nameAdapter['#year(-1)#'], values);
                }
            },
        }, 
        '#year(0)#': { //今年
            name: nameAdapter['#year(0)#'],
            value: () => {
                let values = getYearValues(base, 0);
                if(!range && singleValue){
                    if(singleValue === 'end') return values[1];
                    else if(singleValue === 'start') return values[0];
                }
                return values;
            },
            display: values => {
                if(range){
                    return rangeDisplayFunction(nameAdapter['#year(0)#'], values);
                } else if(singleValue){
                    return singleDisplayFunction(nameAdapter['#year(0)#'], values);
                } else {
                    return rangeForSingleDisplayFunction(nameAdapter['#year(0)#'], values);
                }
            },
        }, 
        '#year(1)#': { //明年
            name: nameAdapter['#year(1)#'],
            value: () => {
                let values = getYearValues(base, 1);
                if(!range && singleValue){
                    if(singleValue === 'end') return values[1];
                    else if(singleValue === 'start') return values[0];
                }
                return values;
            },
            display: values => {
                if(range){
                    return rangeDisplayFunction(nameAdapter['#year(1)#'], values);
                } else if(singleValue){
                    return singleDisplayFunction(nameAdapter['#year(1)#'], values);
                } else {
                    return rangeForSingleDisplayFunction(nameAdapter['#year(1)#'], values);
                }
            },
        },
        '#lastDayOfMonth#': { //本月最后一天
            name: nameAdapter['#lastDayOfMonth#'],
            value: () => {
                let now = DateUtil.validate(base) ? DateUtil.toDayjs(base).toDate() : new Date();
                let dayString = DateUtil.formatValueForDate(DateUtil.getMonthLastDay(now));
                if(!range && singleValue){ //只有非start/end单点日期返回单个值
                    return dayString;
                }
                return [dayString, dayString];
            },
            display: values => {
                if(range){ //区间的value是数组
                    return rangeDisplayFunction(nameAdapter['#lastDayOfMonth#'], values);
                } else if(singleValue) { //start/end的value是单个
                    return singleDisplayFunction(nameAdapter['#lastDayOfMonth#'], values);
                } else { //非start/end的value是数组，但需要显示成单个
                    return singleDisplayFunction(nameAdapter['#lastDayOfMonth#'], values[0]);
                }
            },
        }, 
        '#lastDayOfWeek#': { //上周最后一天
            name: nameAdapter['#lastDayOfWeek#'],
            value: () => {
                let now = DateUtil.validate(base) ? DateUtil.toDayjs(base).toDate() : new Date();
                let dayString = DateUtil.formatValueForDate(DateUtil.getWeekLastDay(now, weekStart));
                if(!range && singleValue){ //只有非start/end单点日期返回单个值
                    return dayString;
                }
                return [dayString, dayString];
            },
            display: values => {
                if(range){ //区间的value是数组
                    return rangeDisplayFunction(nameAdapter['#lastDayOfWeek#'], values);
                } else if(singleValue) { //start/end的value是单个
                    return singleDisplayFunction(nameAdapter['#lastDayOfWeek#'], values);
                } else { //非start/end的value是数组，但需要显示成单个
                    return singleDisplayFunction(nameAdapter['#lastDayOfWeek#'], values[0]);
                }
            },
        }, 
        '#finalDayOfLastMonth#': { //上月最后一天
            name: nameAdapter['#finalDayOfLastMonth#'],
            value: () => {
                let now = DateUtil.validate(base) ? DateUtil.toDayjs(base).toDate() : new Date();
                let month = DateUtil.addMonth(now, -1);
                let dayString = DateUtil.formatValueForDate(DateUtil.getMonthLastDay(month));
                if(!range && singleValue){ //只有非start/end单点日期返回单个值
                    return dayString;
                }
                return [dayString, dayString];
            },
            display: values => {
                if(range){ //区间的value是数组
                    return rangeDisplayFunction(nameAdapter['#finalDayOfLastMonth#'], values);
                } else if(singleValue) { //start/end的value是单个
                    return singleDisplayFunction(nameAdapter['#finalDayOfLastMonth#'], values);
                } else { //非start/end的value是数组，但需要显示成单个
                    return singleDisplayFunction(nameAdapter['#finalDayOfLastMonth#'], values[0]);
                }
            },
        }, 
        '#past#': { 
            name: nameAdapter['#past#'],
            value : () => [nameAdapter['#past#'], nameAdapter['#past#']],
            valueOf: () => [-Infinity, -Infinity],
            display: ()=> [nameAdapter['#past#'], nameAdapter['#past#']],
            type: picker,
        },
        '#future#': { 
            name: nameAdapter['#future#'],
            value : () => [nameAdapter['#future#'], nameAdapter['#future#']],
            valueOf: () => [Infinity, Infinity],
            display: () => [nameAdapter['#future#'], nameAdapter['#future#']],
            type: picker,
        },
        "#lastDayOfYear#": {
            name: nameAdapter['#lastDayOfYear#'],
            value: () => {
                let now = DateUtil.validate(base) ? DateUtil.toDayjs(base).toDate() : new Date();
                let dayString = DateUtil.formatValueForDate(DateUtil.getYearLastDay(now));
                if(!range && singleValue){ //只有非start/end单点日期返回单个值
                    return dayString;
                }
                return [dayString, dayString];
            },
            display: values => {
                if(range){ //区间的value是数组
                    return rangeDisplayFunction(nameAdapter['#lastDayOfYear#'], values);
                } else if(singleValue) { //start/end的value是单个
                    return singleDisplayFunction(nameAdapter['#lastDayOfYear#'], values);
                } else { //非start/end的value是数组，但需要显示成单个
                    return singleDisplayFunction(nameAdapter['#lastDayOfYear#'], values[0]);
                }
            },
        },
    };
    return defaultFunctions;
}

export default { getDefaultDateFunction, getFunctionValue, getFunctionDisplay };

//for test
/*
const multiLang = {
    'page-search-0060':'昨日',
    'page-search-0061': '今日',
    'page-search-0062': '明日',
    'page-search-0063': '上月',
    'page-search-0064': '本月',
    'page-search-0065': '下月',
    'page-search-0066': '上周',
    'page-search-0067': '本周',
    'page-search-0068': '下周',
    'page-search-0069': '上季',
    'page-search-0070': '本季',
    'page-search-0071': '下季',
    'page-search-0072': '去年',
    'page-search-0073': '今年',
    'page-search-0074': '明年',
    'page-search-0075': '本月最后一天',
    'page-search-0076': '本周最后一天',
    'page-search-0077': '上月最后一天',
    'page-search-0100': '至过去', //至过去
    'page-search-0101': '至未来', //至未来
};

const keys = [
    '#day(-1)#',
    '#day(0)#',
    '#day(1)#',
    '#month(-1)#',
    '#month(0)#',
    '#month(1)#',
    '#week(-1)#',
    '#week(0)#',
    '#week(1)#',
    '#quarter(-1)#',
    '#quarter(0)#',
    '#quarter(1)#',
    '#year(-1)#',
    '#year(0)#',
    '#year(1)#',
    '#lastDayOfMonth#',
    '#lastDayOfWeek#',
    '#finalDayOfLastMonth#',
    '#past#', //至过去
    '#future#', //至未来
];

keys.forEach(key =>{
    key = key + ' 11:20:20';
    let value = getFunctionValue(key, { multiLang, range: false });
    let display = getFunctionDisplay(key, { multiLang, range: false });
    console.log(`函数${key}的值为${value},显示为${display}`);
});
*/
